import React, { MouseEventHandler, useState } from "react";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { I } from "services/lang/LanguageService";
import DashboardComponentConfiguratorNew, {
  ConfiguratorModes,
  defaultComponent,
} from "../group-component/GroupComponentConfiguratorNew";
import GroupConfigurator from "./GroupConfigurator";
import VerticalLayout from "../layouts/vertical/VerticalLayout";
import HorizontalFlexLayout from "../layouts/horizontal-flex/HorizontalFlexLayout";
import "../../dashboard-styles/DashboardTabGroup.scss";
import { ReorderType } from "./reorder/DraggableItem";
import Reorder from "./reorder/Reorder";
import MoveGroupOrComponent from "../group-component/MoveGroupOrComponent";
import GroupTitleChangeDialog from "./GroupTitleChangeDialog";
import GroupEditModeButtons from "./GroupEditModeButtons";
import GridLayoutComponent from "../layouts/grid/GridLayout";
import { ContextMenuItem } from "../layouts/interfaces";

interface DashboardTabGroupProps {
  group: DashboardComponentGroup;
  tabId: number;
  onContextMenu: MouseEventHandler<HTMLDivElement>;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
}

const DashboardTabGroup = ({
  group,
  tabId,
  onContextMenu,
  setCurrentContextMenuItems,
}: DashboardTabGroupProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId));
  const isEditMode = useDashboardStore(state => state.isEditMode);

  const [groupConfiguratorVisibility, setGroupConfiguratorVisibility] = useState<boolean>(false);
  const [groupTitleChangeDialogVisibility, setGroupTitleChangeDialogVisibility] =
    useState<boolean>(false);
  const [moveTabGroupDialogVisibility, setMoveTabGroupDialogVisibility] = useState<boolean>(false);
  const [
    dashboardComponentCreationDialogVisibility,
    setDashboardComponentCreationDialogVisibility,
  ] = useState<boolean>(false);
  const [isComponentsReorderEnabled, setIsComponentsReorderEnabled] = useState<boolean>(false);

  const getLayoutComponent = () => {
    switch (group.layout.type) {
      case DashboardLayoutType.VERTICAL:
        return VerticalLayout;
      case DashboardLayoutType.GRID:
        return GridLayoutComponent;
      case DashboardLayoutType.HORIZONTAL_FLEX:
      default:
        return HorizontalFlexLayout;
    }
  };

  const LayoutComponent = getLayoutComponent();
  return (
    <div
      className={`w-full position-relative border-round mt-2 ${
        isEditMode
          ? "border-500 border-2"
          : `${tabContextConfig?.showBorders ? "border-1 border-400" : ""}`
      }`}
      style={{ position: "relative", minHeight: "10rem" }}
    >
      <h4 className="mt-2 ml-2">{I(group.title || "")}</h4>
      {isEditMode && dashboard && (
        <GroupEditModeButtons
          dashboard={dashboard}
          tabId={tabId}
          group={group}
          isComponentsReorderEnabled={isComponentsReorderEnabled}
          setIsComponentsReorderEnabled={setIsComponentsReorderEnabled}
          setGroupConfiguratorVisibility={setGroupConfiguratorVisibility}
          setMoveTabGroupDialogVisibility={setMoveTabGroupDialogVisibility}
          setGroupTitleChangeDialogVisibility={setGroupTitleChangeDialogVisibility}
          setDashboardComponentCreationDialogVisibility={
            setDashboardComponentCreationDialogVisibility
          }
        />
      )}
      <LayoutComponent
        components={group.components}
        layoutType={group.layout.type}
        groupId={group.id}
        align={group.layout.align}
        justify={group.layout.justify}
        onContextMenu={onContextMenu}
        setCurrentContextMenuItems={setCurrentContextMenuItems}
      />
      {isComponentsReorderEnabled && (
        <Reorder
          setReorderDialogVisibility={setIsComponentsReorderEnabled}
          reorderDialogVisibility={isComponentsReorderEnabled}
          reorderType={ReorderType.COMPONENTS}
          group={group}
          tabId={tabId}
        />
      )}
      {dashboardComponentCreationDialogVisibility && isEditMode && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={dashboardComponentCreationDialogVisibility}
          mode={ConfiguratorModes.ADD}
          setIsDialogVisible={setDashboardComponentCreationDialogVisibility}
          component={defaultComponent(group.layout.type)}
          layoutType={group.layout.type}
          groupId={group.id}
        />
      )}
      {groupConfiguratorVisibility && isEditMode && (
        <GroupConfigurator
          group={group}
          isDialogVisible={groupConfiguratorVisibility}
          setIsDialogVisible={setGroupConfiguratorVisibility}
        />
      )}
      {groupTitleChangeDialogVisibility && dashboard && (
        <GroupTitleChangeDialog
          dashboard={dashboard}
          tabId={tabId}
          group={group}
          groupTitleChangeDialogVisibility={groupTitleChangeDialogVisibility}
          setGroupTitleChangeDialogVisibility={setGroupTitleChangeDialogVisibility}
        />
      )}
      {moveTabGroupDialogVisibility && dashboard && (
        <MoveGroupOrComponent
          dashboardId={dashboard?.id}
          isVisible={moveTabGroupDialogVisibility}
          setIsVisible={setMoveTabGroupDialogVisibility}
          groupId={group.id}
          group={group}
          tabId={tabId}
          isComponent={false}
        />
      )}
    </div>
  );
};

export default DashboardTabGroup;
